// NewMemberRegistration.tsx
import React, {useEffect, useState} from 'react';
import { Steps, Button, message, Form, notification } from 'antd';
import NewMemberCustomForm from './NewMemberCustomForm';
import HealthForm from './health/HealthForm';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { parseSchema, schemaToAnswers } from './forms/schemaToAnswers';
import { DEFAULT_HEALTH_SCHEMA } from './forms/defaultHealthSchema';
import { parseJoiningFee } from './joiningFee';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { CreateMemberRequest } from '../../requests/member/CreateMemberRequest';
import HealthFormSubmissionRequest from '../../requests/member/health/HealthFormSubmissionRequest';
import { useClient } from '../../common/ClientProvider';
import { CreateDirectDebitRequest } from '../../requests/payments/CreateDirectDebitRequest';
import { MembershipCategory } from '../../model/members/MembershipCategory';
import { CreatePointOfSaleTransactionRequest } from '../../model/pointofsale/CreatePointOfSaleTransactionRequest';
import { PointOfSaleItem } from '../../model/pointofsale/PointOfSaleItem';
import { usePointOfSaleService } from '../../services/pointofsale/PointOfSaleServiceProvider';
import { useAuth0 } from '@auth0/auth0-react';
import dayjs from 'dayjs';
import { useMember } from '../../common/member/MemberContext';
import { forceTokenRefresh } from '../../common/auth/tokenRefreshCoordinator';
import { AdminPermissions } from '../../model/auth/Permissions';
import { calculateEndDate } from '../../utils/membershipDateUtils';
import { toDateOnly } from '../../utils/DateOnly';
import { DATE_OF_BIRTH_NOT_PROVIDED, isDirectDebitComplete } from './profile/v2/completion';

const { Step } = Steps;

const NewMemberRegistration: React.FC = () => {    
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const [memberFormData, setMemberFormData] = useState(null);
    const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
    const [quickSaveEnabled, setQuickSaveEnabled] = useState(false);
    // Guards the final Submit against double-firing. Without it a double-click sends two
    // POST /members/create requests, so one wins (200) and its twin 409s on the email-unique
    // constraint (FORT-289).
    const [submitting, setSubmitting] = useState(false);
    const memberService = useMemberService();
    const pointOfSaleService = usePointOfSaleService();
    const client = useClient();
    const clientSettings = useClientConfig();
    // The health/PARQ questionnaire is driven by the client's forms.health
    // config; fall back to the verbatim original PARQ when none is configured.
    const healthSchema = React.useMemo(() => {
        const parsed = parseSchema(clientSettings.forms?.healthJson);
        return parsed.sections.length > 0 ? parsed : DEFAULT_HEALTH_SCHEMA;
    }, [clientSettings.forms?.healthJson]);
    // Joining fee (amount / POS item / label / enabled) is client-configurable.
    const joiningFee = React.useMemo(
        () => parseJoiningFee(clientSettings.fees?.joiningJson),
        [clientSettings.fees?.joiningJson],
    );
    const { getAccessTokenSilently } = useAuth0();
    const [membershipCategories, setMembershipCategories] = useState<MembershipCategory[]>([]);
    const [memberJoiningFeePosItem, setMemberJoiningFeePosItem] = useState<PointOfSaleItem>();
    const { isLoading: permissionsLoading, permissions } = useMember();

    useEffect(() => {
        console.log('Effect triggered', { memberFormData, currentStep });
        if (memberFormData) {
            console.log('Setting form values', memberFormData);
            form.setFieldsValue(memberFormData);
        }

        const fetchMembershipCategories = async () => {
            const categories = await memberService.getMembershipCategories();
            setMembershipCategories(categories.filter(cat => cat.isVisible));
        }
        fetchMembershipCategories();
        const fetchMemberJoiningFeePosItem = async () => {
            const item = await pointOfSaleService.getPosItem(joiningFee.posItemName);
            setMemberJoiningFeePosItem(item);
        }
        fetchMemberJoiningFeePosItem();
    }, [currentStep, memberFormData, memberService, form, pointOfSaleService, joiningFee.posItemName]);

    // Monitor form field changes for quick save button using Form.useWatch
    const firstName = Form.useWatch('firstName', form);
    const surname = Form.useWatch('surname', form);
    const email = Form.useWatch('email', form);

    useEffect(() => {
        const enabled = !!(firstName && surname && email);
        setQuickSaveEnabled(enabled);
    }, [firstName, surname, email]);

    const steps = [
        {
            title: 'Member Details',
            content: <NewMemberCustomForm form={form} membershipCategories={membershipCategories} joiningFee={joiningFee} />,
        },
        {
            title: 'Health Questionnaire',
            content: <HealthForm form={form} schema={healthSchema} />,
        }
    ];

    const next = async () => {
        try {
            // Validate current step's fields
            const values = await form.validateFields();
            setMemberFormData(prev => ({ ...prev || {}, ...values }));
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const prev = () => {
        setCurrentStep(currentStep - 1);
    };

    const refreshAuth0Token = async () => {
        try {
            // Force a genuinely new token. Without cacheMode: 'off' Auth0 returns the cached
            // token and the refresh is a no-op, leaving stale permissions in place. Routed
            // through the shared coordinator (FORT-293); bypassInFlight because this is a
            // causal refresh that must observe the member-create write, not coalesce onto a
            // pre-write refresh.
            await forceTokenRefresh(getAccessTokenSilently, { bypassInFlight: true });
            console.log('Auth0 token refreshed successfully');
        } catch (error) {
            console.error('Error refreshing Auth0 token:', error);
        }
    };

    const handleSubmit = async () => {
        // Re-entrancy guard: ignore a second click while a submission is in flight.
        if (submitting) return;
        setSubmitting(true);
        try {
            const finalValues = await form.validateFields();
            const allFormData = { ...memberFormData || {}, ...finalValues };

            // First create the member
            const memberRequest = createMemberRequest(allFormData, client.currentClientId);
            await memberService.create(memberRequest);

            // Refresh Auth0 token to pick up latest permissions
            await refreshAuth0Token();

            setIsRegistrationComplete(true);
            message.success('Registration completed successfully!');
        } catch (error) {
            if (error instanceof Error) {
                notification.error({
                    message: 'Error',
                    description: error.message || 'An error occurred while creating the member',
                });
            } else {
                notification.error({
                    message: 'Error',
                    description: 'An unknown error occurred while creating the member',
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const createDirectDebitRequest = (formData: any): CreateDirectDebitRequest => {
        const request: CreateDirectDebitRequest = {
            accountHolderName: formData.accountHolderName,
            bankName: formData.bankName,
            accountNumber: formData.accountNumber,
            sortCode: formData.sortCode.replace(/-/g, ''),
            membershipId: ''
        };

        return request;
    }

    // Metadata the operator typed on the personal-details step. Shared by the full
    // registration and Quick Save so the quick path can't silently drop it — only
    // keys with a real value are included, never a placeholder.
    const buildMetadata = (formData: any): Record<string, any> => {
        const metadata: Record<string, any> = {};
        if (formData.interests && Array.isArray(formData.interests) && formData.interests.length > 0) {
            metadata.Interests = formData.interests;
        }
        if (formData.emergencyContactName) {
            metadata.EmergencyContact = {
                name: formData.emergencyContactName,
                phoneNumber: formData.emergencyContactPhone || null,
                emailAddress: formData.emergencyContactEmail || null,
            };
        }
        // Company is only collected when the client opts in via signup.collectCompanyName.
        if (clientSettings.signup?.collectCompanyName && formData.companyName?.trim()) {
            metadata.CompanyName = formData.companyName.trim();
        }
        return metadata;
    };

    const createMemberRequest = (formData: any, clientName: string): CreateMemberRequest => {
        const healthFormRequest = createHealthFormRequest(formData);
        const directDebitRequest = createDirectDebitRequest(formData);
        const membershipPaymentRequest = createMembershipPaymentRequest(formData);
        
        // Find the full membership category object based on the selected name
        const selectedCategory = membershipCategories.find(cat => cat.name === formData.membershipCategory);
        
        if (!selectedCategory) {
            throw new Error('Selected membership category not found');
        }

        // Use the start date from the form, or default to today if not provided
        const startDateObj = formData.startDate ?? dayjs();
        const startDate = startDateObj.format('YYYY-MM-DD');

        // Derive valid_to from the client's billing cycle (anchor day + pro-rata
        // cut-off) via the shared helper, so the membership end date matches the
        // pro-rata amount charged. Falls back to month-end when unconfigured.
        const validTo = calculateEndDate(startDateObj, selectedCategory, clientSettings.billing).format('YYYY-MM-DD');

        const metadata = buildMetadata(formData);

        const request: CreateMemberRequest = {
            name: `${formData.firstName} ${formData.surname}`,
            username: formData.email,
            countyState: formData.countyState,
            // toDateOnly, not the raw dayjs: JSON.stringify sends a dayjs via
            // toISOString(), so a date picked at local midnight in the UK during BST
            // goes over the wire as the PREVIOUS day (4 May -> 1990-05-03T23:00Z) and
            // is stored a day early. See utils/DateOnly.
            dateOfBirth: toDateOnly(formData.dateOfBirth),
            emailAddress: formData.email,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            townCity: formData.townCity,
            postZipCode: formData.postcode,
            phoneNumber: formData.phoneNumber,
            country: formData.country,
            receiveEmails: formData.specialOffers === 'Yes',
            status: 'Active',
            membershipCategory: selectedCategory,  // Use the full category object
            clientName,
            healthForm: healthFormRequest,
            directDebitRequest: directDebitRequest,
            membershipPaymentRequest: membershipPaymentRequest,
            validFrom: startDate,
            validTo: validTo,
            isEnabled: false,
            creationType: 'Default',
            metadata
        };

        return request;
    };


    const createMembershipPaymentRequest = (formData: any): CreatePointOfSaleTransactionRequest => {
        return {
            membershipId: '',
            items: [
                {
                    item: memberJoiningFeePosItem!,
                    quantity: 1,
                    overridePrice: formData.proRataAmount
                }
            ],
            amount: formData.proRataAmount,
            bookingSlots: [],
            paymentTransactionStatus: "Pending"
        };
    };

    const hasAdminPermission = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.ClubAdmin ||
            p.name === AdminPermissions.FortiusAdmin
        );
    };

    const isFrontOfHouse = () => {
        if (permissionsLoading || !permissions) return false;
        return permissions.permissions.some(p =>
            p.name === AdminPermissions.FrontOfHouse ||
            p.name === AdminPermissions.FortiusAdmin
        );
    };

    const canQuickSave = () => {
        return hasAdminPermission() || isFrontOfHouse();
    };



    const createQuickSaveMemberRequest = (formData: any, clientName: string): CreateMemberRequest => {
        // Save whatever plan the operator has actually chosen. With nothing chosen we
        // send NO category and let the server assign the hidden "Default New Member"
        // placeholder — never a guess at membershipCategories[0], which is what put
        // quick-created members onto whichever real priced plan happened to sort first.
        const selectedCategory =
            membershipCategories.find(cat => cat.name === formData.membershipCategory) ?? null;

        // Same for the mandate: use what's on the form if the operator filled it in,
        // and send nothing at all if they didn't. isDirectDebitComplete is the shared
        // rule (non-empty holder + bank, 8-digit account, 6-digit sort) so this agrees
        // with the self-service create and the profile screen.
        const enteredMandate = {
            accountHolderName: formData.accountHolderName ?? '',
            bankName: formData.bankName ?? '',
            accountNumber: formData.accountNumber ?? '',
            sortCode: (formData.sortCode ?? '').replace(/-/g, ''),
        };
        const directDebitRequest: CreateDirectDebitRequest | null = isDirectDebitComplete(enteredMandate)
            ? { ...enteredMandate, membershipId: '' }
            : null;

        const startDateObj = formData.startDate ?? dayjs();
        const startDate = startDateObj.format('YYYY-MM-DD');
        const validTo = calculateEndDate(startDateObj, selectedCategory, clientSettings.billing).format('YYYY-MM-DD');

        // Same rule as the fields below: keep whatever the operator entered. This was
        // hardcoded to {}, silently discarding Interests / EmergencyContact / CompanyName
        // typed on the personal-details step before they hit Quick Save.
        const metadata = buildMetadata(formData);

        // Every field below is whatever the operator has typed so far, blank if they
        // haven't. Quick Save must NEVER substitute a stand-in value: "To be Completed"
        // and "00000000000" are non-empty strings and a date 18 years ago is a valid
        // date, so all of them read downstream as real answers — which is how
        // quick-created members ended up with a "complete" address and date of birth
        // they were never asked for, and were never prompted to fill them in.
        const request: CreateMemberRequest = {
            name: `${formData.firstName} ${formData.surname}`,
            username: formData.email,
            countyState: formData.countyState ?? '',
            // The explicit not-provided sentinel (DateTime.MinValue) when the operator
            // hasn't entered one, so the payload states that no date was collected
            // rather than leaving the server to infer it from an absent key. NOT null —
            // that would fail to bind to the non-nullable DateOfBirth.
            dateOfBirth: toDateOnly(formData.dateOfBirth) ?? DATE_OF_BIRTH_NOT_PROVIDED,
            emailAddress: formData.email,
            addressLine1: formData.addressLine1 ?? '',
            addressLine2: formData.addressLine2 ?? '',
            townCity: formData.townCity ?? '',
            // NB the form field is `postcode`, not `postZipCode` — same as the full
            // registration path above. Reading the wrong name silently drops it.
            postZipCode: formData.postcode ?? '',
            phoneNumber: formData.phoneNumber ?? '',
            country: formData.country ?? 'United Kingdom',
            receiveEmails: false,
            status: 'Inactive',
            membershipCategory: selectedCategory,
            membershipPaymentRequest: null,
            clientName,
            healthForm: { answers: [] }, // Empty health form for quick save
            // Null when the operator left the mandate blank — the server then skips
            // ProcessDirectDebitSetup entirely. It used to write a placeholder
            // ("To be Completed" / 00000000 / 000000) regardless, which reads as a real
            // mandate to the direct-debit screen's has-a-mandate check and blocked the
            // member from ever adding their own.
            directDebitRequest,
            validFrom: startDate,
            validTo: validTo,
            isEnabled: false,
            creationType: 'Default',
            metadata
        };

        return request;
    };

    const handleQuickSave = async () => {
        try {
            const formData = form.getFieldsValue();
            
            // Create the member with default values
            const memberRequest = createQuickSaveMemberRequest(formData, client.currentClientId);
            await memberService.create(memberRequest);
            
            
            // Refresh Auth0 token to pick up latest permissions
            await refreshAuth0Token();
            
            setIsRegistrationComplete(true);
            message.success('Quick save completed successfully! Member created with default values.');
        } catch (error) {
            if (error instanceof Error) {
                notification.error({
                    message: 'Error',
                    description: error.message || 'An error occurred during quick save',
                });
            } else {
                notification.error({
                    message: 'Error',
                    description: 'An unknown error occurred during quick save',
                });
            }
        }
    };

    // Generic serialisation driven by the same schema the form rendered, so the
    // persisted {question, answer} pairs stay byte-identical to the original
    // hardcoded mapping for the ported Southbank PARQ, and adapt automatically
    // to a client's custom questionnaire.
    const createHealthFormRequest = (values: any): HealthFormSubmissionRequest => {
        return { answers: schemaToAnswers(healthSchema, values) };
    }

    return (
        <div>
            <Steps current={currentStep} style={{ marginBottom: 24 }}>
                {steps.map(item => (
                    <Step key={item.title} title={item.title} />
                ))}
            </Steps>

            {/* Quick Save button - only visible for admins and front of house at the top */}
            {canQuickSave() && currentStep === 0 && !isRegistrationComplete && (
                <div style={{ 
                    marginBottom: 16, 
                    textAlign: 'right',
                    padding: '8px 0',
                    borderBottom: '1px solid #f0f0f0'
                }}>
                    <Button 
                        type="primary" 
                        onClick={handleQuickSave}
                        disabled={!quickSaveEnabled}
                        size="large"
                        style={{ 
                            fontWeight: 'bold'
                        }}
                    >
                        Quick Save
                    </Button>
                </div>
            )}

            <div className="steps-content">{steps[currentStep].content}</div>

            <div className="steps-action" style={{ marginTop: 24 }}>
                {currentStep > 0 && (
                    <Button style={{ margin: '0 8px' }} onClick={prev} icon={<LeftOutlined />}>
                        Previous
                    </Button>
                )}
                {currentStep < steps.length - 1 && (
                    <Button type="primary" onClick={next} icon={<RightOutlined />}>
                        Next
                    </Button>
                )}
                {currentStep === steps.length - 1 && !isRegistrationComplete && (
                    <Button type="primary" onClick={handleSubmit} loading={submitting} disabled={submitting}>
                        Submit
                    </Button>
                )}
            </div>
        </div>
    );
};

export default NewMemberRegistration;